import { Box } from 'designSystem/components/Box'
import { Separator } from 'designSystem/components/Separator'
import { DocumentMetadataEditSection } from 'features/knowledgeDocumentViewer/components/metadata/DocumentMetadataEditSection'
import { metadataFillLayout } from 'features/knowledgeDocumentViewer/components/metadata/metadataFillLayout'
import { PageMetadataEditSection } from 'features/knowledgeDocumentViewer/components/metadata/PageMetadataEditSection'
import type {
  DocumentMetadataUserEditEntry,
  DocumentMetadataUserEditValues,
  DocumentPageMetadataUserEditEntry,
  DocumentPageMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import type { FamilyDocumentMetadataSaveMember } from 'features/knowledgeDocumentViewer/utils/buildFamilyDocumentMetadataUserEditPuts'
import type { FamilyPageMetadataSaveMember } from 'features/knowledgeDocumentViewer/utils/buildFamilyPageMetadataUserEditPuts'
import type { RevisionFamilyMemberView } from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'
import React from 'react'

export type StackedMetadataEditSectionsProps = {
  showDocumentDetails: boolean
  showPageDetails: boolean
  systemDocument: SystemDocumentEntry | undefined
  documentUserEdit: DocumentMetadataUserEditEntry | undefined
  isDocumentLoading: boolean
  canEditDocument: boolean
  isSavingDocumentEdit: boolean
  pageNum: number
  pageEntry: SystemDocumentPageEntry | undefined
  pageUserEdit: DocumentPageMetadataUserEditEntry | undefined
  isPageLoading: boolean
  canEditPage: boolean
  isSavingPageEdit: boolean
  currentBqDocumentId: string | undefined
  currentFilename: string | undefined
  onSaveDocument: (values: DocumentMetadataUserEditValues) => Promise<void>
  onSaveDocumentFamily: (
    values: DocumentMetadataUserEditValues,
    members: readonly FamilyDocumentMetadataSaveMember[]
  ) => Promise<void>
  onSavePage: (values: DocumentPageMetadataUserEditValues) => Promise<void>
  onSavePageFamily: (
    values: DocumentPageMetadataUserEditValues,
    members: readonly FamilyPageMetadataSaveMember[]
  ) => Promise<void>
  onDocumentEditingChange?: (isEditing: boolean) => void
  onPageEditingChange?: (isEditing: boolean) => void
  onSelectVersion?: (member: RevisionFamilyMemberView) => void
  onOpenInNewTab?: (member: RevisionFamilyMemberView) => void
  documentFallback?: React.ReactNode
  includeInternalIds?: boolean
  hideDocumentRevision?: boolean
  fillAvailable?: boolean
  showRevisionDropdown?: boolean
}

export function StackedMetadataEditSections({
  showDocumentDetails,
  showPageDetails,
  systemDocument,
  documentUserEdit,
  isDocumentLoading,
  canEditDocument,
  isSavingDocumentEdit,
  pageNum,
  pageEntry,
  pageUserEdit,
  isPageLoading,
  canEditPage,
  isSavingPageEdit,
  currentBqDocumentId,
  currentFilename,
  onSaveDocument,
  onSaveDocumentFamily,
  onSavePage,
  onSavePageFamily,
  onDocumentEditingChange,
  onPageEditingChange,
  onSelectVersion,
  onOpenInNewTab,
  documentFallback,
  includeInternalIds = false,
  hideDocumentRevision = false,
  fillAvailable = false,
  showRevisionDropdown = false,
}: StackedMetadataEditSectionsProps): React.ReactElement {
  const documentFills = fillAvailable && !showPageDetails
  const pageFills = fillAvailable && !showDocumentDetails
  const useDocumentFallback =
    documentFallback !== undefined && systemDocument === undefined

  return (
    <Box
      display='flex'
      direction='column'
      flex='one'
      minHeight='none'
      overflow={fillAvailable ? 'hidden' : 'yAuto'}>
      {showDocumentDetails ? (
        <Box
          display='flex'
          direction='column'
          paddingX='xl'
          paddingBottom={showPageDetails ? 'lg' : 'none'}
          {...(documentFills ? metadataFillLayout : {})}>
          {useDocumentFallback ? (
            documentFallback
          ) : (
            <DocumentMetadataEditSection
              document={systemDocument}
              documentUserEdit={documentUserEdit}
              isLoading={isDocumentLoading}
              canEdit={canEditDocument}
              isSaving={isSavingDocumentEdit}
              pageNum={pageNum}
              pageEntry={pageEntry}
              pageUserEdit={pageUserEdit}
              onSave={onSaveDocument}
              onSaveFamily={onSaveDocumentFamily}
              onEditingChange={onDocumentEditingChange}
              onSelectVersion={onSelectVersion}
              onOpenInNewTab={onOpenInNewTab}
              includeInternalIds={includeInternalIds}
              hideRevision={hideDocumentRevision}
            />
          )}
        </Box>
      ) : null}
      {showDocumentDetails && showPageDetails ? <Separator /> : null}
      {showPageDetails ? (
        <Box
          display='flex'
          direction='column'
          paddingX='xl'
          paddingTop={showDocumentDetails ? 'lg' : 'none'}
          {...(pageFills ? metadataFillLayout : {})}>
          <PageMetadataEditSection
            pageEntry={pageEntry}
            pageUserEdit={pageUserEdit}
            pageNum={pageNum}
            isLoading={isPageLoading}
            canEdit={canEditPage}
            isSaving={isSavingPageEdit}
            currentBqDocumentId={currentBqDocumentId}
            currentFilename={currentFilename}
            systemDocument={systemDocument}
            documentUserEdit={documentUserEdit}
            onSave={onSavePage}
            onSaveFamily={onSavePageFamily}
            onEditingChange={onPageEditingChange}
            onSelectVersion={onSelectVersion}
            onOpenInNewTab={onOpenInNewTab}
            includeInternalIds={includeInternalIds}
            showRevisionDropdown={showRevisionDropdown}
          />
        </Box>
      ) : null}
    </Box>
  )
}
